import { Injectable, inject } from '@angular/core';
import {
  WlCanvasPattern as WlCanvasPatternEnum,
  WlCardElevation as WlCardElevationEnum,
  WlSidebarStyle as WlSidebarStyleEnum,
  WlTextTransform as WlTextTransformEnum,
} from '@enrollhere/classes';
import { BehaviorSubject } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { ThemeService } from './theme.service';
import { MetadataService } from './metadata.service';

// Choice-option aliases must stay `Extract<..., string>` so enrollhere-web's
// `ThemeChoiceOption<T extends string>` type-checks. Proto numeric enums are
// kept on WlThemeSettings so WlService.set still accepts classes WlContainer.

type WlSidebarStyleValue =
  | WlSidebarStyleEnum
  | 'WL_SIDEBAR_STYLE_UNKNOWN'
  | 'WL_SIDEBAR_STYLE_LIGHT'
  | 'WL_SIDEBAR_STYLE_INK'
  | 'WL_SIDEBAR_STYLE_BRAND'
  | 'WL_SIDEBAR_STYLE_CUSTOM';

type WlTextTransformValue =
  | WlTextTransformEnum
  | 'WL_TEXT_TRANSFORM_UNKNOWN'
  | 'WL_TEXT_TRANSFORM_NONE'
  | 'WL_TEXT_TRANSFORM_UPPERCASE'
  | 'WL_TEXT_TRANSFORM_LOWERCASE'
  | 'WL_TEXT_TRANSFORM_CAPITALIZE';

type WlCardElevationValue =
  | WlCardElevationEnum
  | 'WL_CARD_ELEVATION_UNKNOWN'
  | 'WL_CARD_ELEVATION_NONE'
  | 'WL_CARD_ELEVATION_SUBTLE'
  | 'WL_CARD_ELEVATION_RAISED';

type WlCanvasPatternValue =
  | WlCanvasPatternEnum
  | 'WL_CANVAS_PATTERN_UNKNOWN'
  | 'WL_CANVAS_PATTERN_NONE'
  | 'WL_CANVAS_PATTERN_DOTS';

export interface WlColorContainer {
  primary?: string | undefined;
  primaryDark?: string | undefined;
  accent?: string | undefined;
  background?: string | undefined;
  surface?: string | undefined;
  text?: string | undefined;
  border?: string | undefined;
  primaryContent?: string | undefined;
  accentHover?: string | undefined;
  accentActive?: string | undefined;
  accentContent?: string | undefined;
  link?: string | undefined;
  linkHover?: string | undefined;
  sidebarContent?: string | undefined;
  sidebarIcon?: string | undefined;
  sidebarFocus?: string | undefined;
  sidebarActiveBackground?: string | undefined;
  sidebarActiveContent?: string | undefined;
  cardIcon?: string | undefined;
  tableHeader?: string | undefined;
}

export type WlSidebarStyle = Extract<WlSidebarStyleValue, string>;
export type WlTextTransform = Extract<WlTextTransformValue, string>;
export type WlCardElevation = Extract<WlCardElevationValue, string>;
export type WlCanvasPattern = Extract<WlCanvasPatternValue, string>;

export interface WlThemeSettings {
  sidebarStyle?: WlSidebarStyleValue | undefined;
  sidebarColor?: string | undefined;
  cornerRadius?: string | undefined;
  controlCornerRadius?: string | undefined;
  headingFont?: string | undefined;
  bodyFont?: string | undefined;
  fontStylesheetUrl?: string | undefined;
  headingWeight?: string | undefined;
  headingLetterSpacing?: string | undefined;
  headingTextTransform?: WlTextTransformValue | undefined;
  tableHeaderFont?: string | undefined;
  tableHeaderFontSize?: string | undefined;
  tableHeaderLetterSpacing?: string | undefined;
  tableHeaderTextTransform?: WlTextTransformValue | undefined;
  cardElevation?: WlCardElevationValue | undefined;
  canvasPattern?: WlCanvasPatternValue | undefined;
  canvasPatternColor?: string | undefined;
  canvasPatternSize?: string | undefined;
  canvasPatternDotSize?: string | undefined;
}

export interface WlLogoContainer {
  light?: string | undefined;
  dark?: string | undefined;
  collapsed?: string | undefined;
  collapsedDark?: string | undefined;
  transparent?: boolean | undefined;
  profile?: string | undefined;
}

export interface WlMetaContainer {
  favicon?: string | undefined;
  title?: string | undefined;
  description?: string | undefined;
}

export interface WlContainer {
  id?: string | undefined;
  logo?: string | undefined;
  logos?: WlLogoContainer | undefined;
  colors?: WlColorContainer | undefined;
  loading?: boolean | undefined;
  collapsed?: boolean | undefined;
  enableDarkMode?: boolean | undefined;
  meta?: WlMetaContainer | undefined;
  customCss?: string | undefined;
  theme?: WlThemeSettings | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class WlService {
  logoUrl?: string;
  loading = true;
  loaded = new BehaviorSubject<boolean>(false);
  loadingChange = new BehaviorSubject<boolean>(true);
  wl$ = new BehaviorSubject<WlContainer | undefined>(undefined);

  private domainConfigMap: Record<string, string> = {
    'leadstarhub.com': 'lds',
  };
  private titleService = inject(Title);
  private themeService = inject(ThemeService);
  private metadataService = inject(MetadataService);
  private domainConfigSet = false;

  constructor() {
    const simulateLeadstar = localStorage.getItem('simulateLeadstar');
    if (simulateLeadstar === 'true') {
      this.domainConfigMap['localhost'] = 'lds';
      this.domainConfigMap['127.0.0.1'] = 'lds';
    }
  }

  private getHostname(): string | undefined {
    return globalThis.location?.hostname?.toLowerCase();
  }

  private detectDomainConfig(): string | undefined {
    const hostname = this.getHostname();
    if (!hostname) {
      return undefined;
    }

    if (this.domainConfigMap[hostname]) {
      return this.domainConfigMap[hostname];
    }

    for (const [domain, configId] of Object.entries(this.domainConfigMap)) {
      if (hostname.endsWith(`.${domain}`) || hostname === domain) {
        return configId;
      }
    }

    return undefined;
  }

  getDomainConfigId(): string | undefined {
    return this.detectDomainConfig();
  }

  isDomainConfigSet(): boolean {
    return this.domainConfigSet;
  }

  simulateLeadstarDomain(): void {
    const currentState = localStorage.getItem('simulateLeadstar') === 'true';
    const newState = !currentState;

    if (newState) {
      localStorage.setItem('simulateLeadstar', 'true');
      this.domainConfigMap['localhost'] = 'lds';
      this.domainConfigMap['127.0.0.1'] = 'lds';
    } else {
      localStorage.removeItem('simulateLeadstar');
      delete this.domainConfigMap['localhost'];
      delete this.domainConfigMap['127.0.0.1'];
    }

    setTimeout(() => {
      window.location.reload();
    }, 500);
  }

  clearLeadstarSimulation(): void {
    localStorage.removeItem('simulateLeadstar');
    delete this.domainConfigMap['localhost'];
    delete this.domainConfigMap['127.0.0.1'];
  }

  isLeadstarSimulationEnabled(): boolean {
    return localStorage.getItem('simulateLeadstar') === 'true';
  }

  initializeFromDomain(): void {
    const configId = this.detectDomainConfig();
    if (configId) {
      this.domainConfigSet = true;
      this.wl$.next({ loading: true });
    } else {
      this.domainConfigSet = false;
      this.titleService.setTitle('EnrollHere');
      this.set(undefined);
    }
  }

  set(wl: WlContainer | undefined): void {
    if (wl) {
      this.wl$.next(wl);
      this.logoUrl = wl.logo;

      this.themeService.setWlContainer(wl);
      this.metadataService.updateMetadata(wl);
    } else {
      this.wl$.next(undefined);

      this.themeService.setWlContainer(undefined);
      this.metadataService.updateMetadata(undefined);
    }

    this.loading = false;
    this.loadingChange.next(false);
    this.loaded.next(true);
  }
}
